import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LockOutlined } from '@mui/icons-material';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';

/**
 * Page de réinitialisation du mot de passe
 * Accessible via un lien reçu par email contenant un access_token dans le fragment URL
 */
const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = 'CryptoNovaX - ' + t('auth.reset.title');
  }, []);

  /**
   * Extrait l'OTP (token_hash) du fragment URL et initialise une session via verifyOtp
   */
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      // Extraire l'OTP du fragment #otp=...
      const otpMatch = hash.match(/otp=([^&]+)/);
      if (otpMatch && otpMatch[1]) {
        const tokenHash = decodeURIComponent(otpMatch[1]);
        setOtp(tokenHash);

        // Vérifier l'OTP (type recovery) pour créer une session active côté client
        (async () => {
          const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' as any });
          if (error) {
            setError(t('auth.reset.errorOTP1'));
            setTokenValid(false);
            return;
          }
          setTokenValid(true);

          // Optionnel: récupérer l'email depuis la session
          const userEmail = data?.user?.email || data?.session?.user?.email || '';
          if (userEmail) {
            setEmail(userEmail);
          }
        })();
      } else {
        setError(t('auth.reset.errorOTP2'));
      }
    } else {
      setError(t('auth.reset.errorOTP3'));
    }
  }, [location.hash]);

  /**
   * Gère la soumission du formulaire de réinitialisation
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validation des mots de passe
    if (newPassword !== confirmPassword) {
      setError(t('auth.reset.errorPassword1'));
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError(t('auth.reset.errorPassword2'));
      setLoading(false);
      return;
    }

    try {
      // Mise à jour du mot de passe directement côté client avec la session active
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        throw new Error(updateError.message || t('auth.reset.errorPassword3'));
      }
      setMessage(t('auth.reset.msgPasswordOk'));
      
      // Rediriger vers la page de connexion après un délai
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || t('auth.reset.errorPassword4'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        background: '#ffffff',
        p: 0,
        m: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100vh',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {/* Left Column - Form */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '340px' }}>
            <Box sx={{ mb: 5, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666', 
                  mb: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  letterSpacing: '0.02em',
                }}
              >
                {t('auth.forgot.welcome')}
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 0, 
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  letterSpacing: '-0.02em',
                }}
              >
                CryptoNovaX
              </Typography>
            </Box>

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#000',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  mb: 1,
                }}
              >
                {t('auth.reset.newPassword')}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontSize: '0.875rem',
                }}
              >
                {t('auth.reset.newPasswordLabel')}
              </Typography>
            </Box>

            {message && (
              <Alert
                severity="success"
                sx={{
                  mb: 2.5,
                  backgroundColor: 'rgba(76, 175, 80, 0.08)',
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                  color: '#2e7d32',
                  borderRadius: '8px',
                }}
              >
                {message}
                <br />
                <small>{t('auth.reset.msgRedirect')}</small>
              </Alert>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2.5,
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  color: '#d32f2f',
                  borderRadius: '8px',
                }}
              >
                {error}
              </Alert>
            )}

            {tokenValid ? (
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder={t('auth.reset.newPassword')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: '#999', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: '#999' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f8f8f8',
                      borderRadius: '8px',
                      height: '48px',
                      border: '1px solid #e0e0e0',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#d0d0d0',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        borderColor: '#1a1a1a',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#000',
                      fontSize: '0.875rem',
                      '&::placeholder': {
                        color: '#999',
                        opacity: 1,
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder={t('auth.register.confirmPasswordLabel')}
                  error={confirmPassword !== '' && newPassword !== confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: '#999', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: '#999' }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f8f8f8',
                      borderRadius: '8px',
                      height: '48px',
                      border: '1px solid #e0e0e0',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#d0d0d0',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        borderColor: '#1a1a1a',
                      },
                      '&.Mui-error': {
                        borderColor: '#d32f2f',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#000',
                      fontSize: '0.875rem',
                      '&::placeholder': {
                        color: '#999',
                        opacity: 1,
                      },
                    },
                  }}
                  helperText={
                    confirmPassword !== '' && newPassword !== confirmPassword
                      ? t('auth.register.passwordsNoMatch')
                      : ''
                  }
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={
                    loading || 
                    !newPassword || 
                    !confirmPassword || 
                    newPassword !== confirmPassword ||
                    newPassword.length < 6
                  }
                  sx={{
                    backgroundColor: '#f8f8f8',
                    color: '#000',
                    height: '48px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    mb: 3,
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      backgroundColor: '#ebebeb',
                      boxShadow: 'none',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#f8f8f8',
                      color: '#999',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t('auth.reset.title')
                  )}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    to="/login"
                    style={{
                      color: '#000',
                      textDecoration: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 400,
                    }}
                  >
                    {t('auth.forgot.return')}
                  </Link>
                </Box>
              </Box>
            ) : (
              <Box>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                    border: '1px solid rgba(244, 67, 54, 0.2)',
                    color: '#d32f2f',
                    borderRadius: '8px',
                  }}
                >
                  {t('auth.reset.msgErrorLink')}
                </Alert>
                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    to="/forgot-password"
                    style={{
                      color: '#000',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}
                  >
                    {t('auth.reset.msgAskNewLink')}
                  </Link>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Column - Image */}
        <Box
          sx={{
            width: { xs: '0%', md: '50%' },
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #4a6fa5 0%, #5b7db8 15%, #d97c91 45%, #e85d72 65%, #1a1a1a 100%)',
          }}
        >
          {/* Overlay shapes */}
          <Box
            sx={{
              position: 'absolute',
              top: '15%',
              right: '10%',
              width: '300px',
              height: '400px',
              background: 'linear-gradient(135deg, rgba(255, 100, 120, 0.6) 0%, rgba(255, 100, 120, 0.3) 100%)',
              filter: 'blur(80px)',
              transform: 'rotate(-15deg)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              left: '15%',
              width: '250px',
              height: '350px',
              background: 'linear-gradient(135deg, rgba(255, 100, 120, 0.5) 0%, rgba(255, 100, 120, 0.2) 100%)',
              filter: 'blur(70px)',
              transform: 'rotate(25deg)',
            }}
          />

          {/* Trust badges - Center */}
          <Box sx={{ 
            position: 'absolute',
            top: '50%',
            left: '55%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 3.5,
            width: '85%',
            maxWidth: '420px',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                🔒
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.reset.cardTitle1')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.reset.cardSubtitle1')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                ⚡
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.reset.cardTitle2')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.reset.cardSubtitle2')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                🛡️
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.reset.cardTitle3')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.reset.cardSubtitle3')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                💪
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.reset.cardTitle4')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.reset.cardSubtitle4')}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Bottom attribution */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 32,
              left: 32,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px solid rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: '#ffffff',
                fontSize: '0.6875rem',
                fontWeight: 400,
                letterSpacing: '0.01em',
                opacity: 0.9,
              }}
            >
              {t('auth.reset.message')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;